import GAIA from "@vectorlab-cg/gaia";
import getGaiaConfig from "./gaiaConfig";

export class GaiaWidget {
    private container: HTMLElement;
    private toggleButton: HTMLButtonElement;
    private widgetBody: HTMLElement;
    private canvas: HTMLCanvasElement;
    private inputField: HTMLInputElement;
    private messagesContainer: HTMLElement;

    private gaia: GAIA | null = null;
    private isInitialized: boolean = false;
    private isOpen: boolean = false;

    constructor() {
        this.container = document.createElement("div");
        this.toggleButton = document.createElement("button");
        this.widgetBody = document.createElement("div");
        this.canvas = document.createElement("canvas");
        this.inputField = document.createElement("input");
        this.messagesContainer = document.createElement("div");

        this.createDOM();
        this.bindEvents();

        this.initGaia();
    }

    private createDOM(): void {
        this.container.id = "gaia-widget-container";

        // toggle button
        this.toggleButton.id = "gaia-toggle-button";
        this.toggleButton.innerHTML = "Talk to GAIA";

        // widget body (initially hidden via CSS class)
        this.widgetBody.id = "gaia-widget-body";

        // close button
        const closeBtn = document.createElement("button");
        closeBtn.id = "gaia-close-button";
        closeBtn.innerHTML = "&times;";
        closeBtn.title = "Hide GAIA";
        this.widgetBody.appendChild(closeBtn);
        closeBtn.addEventListener("click", () => this.toggleWidget());

        // avatar canvas setup
        const canvasContainer = document.createElement("div");
        canvasContainer.id = "gaia-canvas-container";

        this.canvas.id = "gaia-avatar-canvas";
        canvasContainer.appendChild(this.canvas);
        this.widgetBody.appendChild(canvasContainer);

        // messages container
        this.messagesContainer.id = "gaia-messages-container";
        this.widgetBody.appendChild(this.messagesContainer);

        // input field
        const inputContainer = document.createElement("div");
        inputContainer.id = "gaia-input-container";

        this.inputField.type = "text";
        this.inputField.id = "gaia-input"; // matches "elementId" config component
        this.inputField.placeholder = "Type your message...";
        this.inputField.autocomplete = "off";

        inputContainer.appendChild(this.inputField);
        this.widgetBody.appendChild(inputContainer);

        this.container.appendChild(this.widgetBody);
        this.container.appendChild(this.toggleButton);
        document.body.appendChild(this.container);
    }

    private bindEvents(): void {
        this.toggleButton.addEventListener("click", () => this.toggleWidget());

        this.inputField.addEventListener("keydown", (e: KeyboardEvent) => {
            if (e.key === "Enter" && !this.isInitialized) {
                e.preventDefault();
                this.showWarningMessage("GAIA is still loading, please wait...");
            }
        });
    }

    private toggleWidget(): void {
        this.isOpen = !this.isOpen;
        if (this.isOpen) {
            this.widgetBody.classList.add("gaia-widget-open");
            this.toggleButton.style.display = "none";
            this.inputField.focus();

            if (!this.isInitialized) {
                this.showWarningMessage("GAIA is still loading, please wait...");
            }
        } else {
            this.widgetBody.classList.remove("gaia-widget-open");
            this.toggleButton.style.display = "block";

            if (this.gaia && this.isInitialized) {
                this.gaia.abort().catch((error: any) => {
                    console.error("Failed to abort GAIA", error);
                });
            }
        }
    }

    private async initGaia(): Promise<void> {
        try {
            const config = getGaiaConfig(this.canvas);
            this.gaia = await GAIA.create(config);

            await this.gaia.activate();

            this.gaia.on("user-typed", this.handleUserTyped.bind(this));
            this.gaia.on("avatar-speech", this.handleAvatarSpeech.bind(this));

            this.isInitialized = true;
        } catch (error) {
            console.error("Failed to initialize GAIA", error);
            this.showWarningMessage("Failed to load GAIA.");
        }
    }

    private handleUserTyped(text: string): void {
        this.showMessage(text, "user");
        this.inputField.value = "";
    }

    private handleAvatarSpeech(text: string): void {
        this.showMessage(text, "avatar");
    }

    private showMessage(text: string, type: "user" | "avatar" | "system"): void {
        const msgEl = document.createElement("div");
        msgEl.className = `gaia-message gaia-message-${type}`;
        msgEl.textContent = text;

        this.messagesContainer.appendChild(msgEl);

        // remove after X seconds
        setTimeout(() => {
            msgEl.style.opacity = "0";
            setTimeout(() => {
                if (msgEl.parentElement) {
                    msgEl.parentElement.removeChild(msgEl);
                }
            }, 1500);
        }, 5000 + (type === "user" ? 0 : 2000));
    }

    private showWarningMessage(text: string): void {
        this.showMessage(text, "system");
    }
}
